import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { X, Ticket, AlertCircle, Plus, Minus, Info } from "lucide-react";
import eventService from "../../services/eventService";
import { useNotifications } from "../../contexts/NotificationContext";
import { useSelectedEvent } from "../../contexts/SelectedEventContext";

const ModalAumentarBoletos = ({ isOpen, onClose, deuda, onBoletosActualizados }) => {
  const { showSuccess, showError } = useNotifications();
  const { eventoActual } = useSelectedEvent();
  const [nuevaCantidad, setNuevaCantidad] = useState(deuda?.asistente?.cantidad_boletos || 0);
  const [opcionProrrateo, setOpcionProrrateo] = useState("crear_nuevas");
  const [loading, setLoading] = useState(false);

  if (!deuda) return null;

  const cantidadActual = deuda.asistente.cantidad_boletos;
  const facturas = Array.isArray(deuda.facturas) ? deuda.facturas : (Array.isArray(deuda.financiero?.facturas) ? deuda.financiero.facturas : []);
  const precioPorBoleto = eventoActual?.costo || 0;

  // Monto pagado total del asistente
  const montoPagadoTotal = Number(deuda.financiero?.monto_pagado || 0) || 
    facturas.reduce((sum, f) => sum + (Number(f.monto_pagado) || 0), 0);

  // Cantidad de boletos que ya cuentan con pago total o abono parcial
  // Mínimo 1 (el propio graduado nunca se puede cancelar a 0)
  const boletosPagadosOConAbono = precioPorBoleto > 0
    ? Math.max(1, Math.ceil(montoPagadoTotal / precioPorBoleto))
    : 1;

  // Boletos cancelables directamente (únicamente los que están 100% pendientes de pago)
  const boletosCancelables = Math.max(0, cantidadActual - boletosPagadosOConAbono);
  const minimaCantidadPermitida = boletosPagadosOConAbono;

  const cantidadValida = nuevaCantidad !== "" && !isNaN(Number(nuevaCantidad)) && Number(nuevaCantidad) >= 0;
  const numNuevaCantidad = cantidadValida ? Number(nuevaCantidad) : cantidadActual;

  const diferencia = cantidadValida ? numNuevaCantidad - cantidadActual : 0;
  const esDisminucion = diferencia < 0;
  const esInvalidaDisminucion = cantidadValida && numNuevaCantidad < minimaCantidadPermitida;
  const costoAdicional = diferencia > 0 ? precioPorBoleto * diferencia : 0;
  const montoReduccion = diferencia < 0 ? precioPorBoleto * Math.abs(diferencia) : 0;

  const handleIncrement = () => {
    const current = typeof nuevaCantidad === "number" ? nuevaCantidad : parseInt(nuevaCantidad, 10) || cantidadActual;
    setNuevaCantidad(current + 1);
  };

  const handleDecrement = () => {
    const current = typeof nuevaCantidad === "number" ? nuevaCantidad : parseInt(nuevaCantidad, 10) || cantidadActual;
    if (current > 1) {
      setNuevaCantidad(current - 1);
    }
  };

  const handleInputChange = (e) => {
    const rawVal = e.target.value;
    if (rawVal === "") {
      setNuevaCantidad("");
      return;
    }
    const val = parseInt(rawVal, 10);
    if (!isNaN(val) && val >= 0) {
      setNuevaCantidad(val);
    }
  };

  const handleInputBlur = () => {
    if (nuevaCantidad === "" || isNaN(Number(nuevaCantidad))) {
      setNuevaCantidad(cantidadActual);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cantidadValida || numNuevaCantidad === cantidadActual) {
      showError("La nueva cantidad debe ser diferente a la actual");
      return;
    }

    if (numNuevaCantidad < minimaCantidadPermitida) {
      showError(`La cantidad mínima permitida para disminuir es ${minimaCantidadPermitida}. Para cancelar boletos pagados, usa Devoluciones.`);
      return;
    }

    setLoading(true);

    try {
      // Para disminución, siempre usar "eliminar_completas"
      const opcionFinal = esDisminucion ? "eliminar_completas" : opcionProrrateo;
      
      const resultado = await eventService.updateInvitadoBoletos(
        deuda.invitado_id,
        numNuevaCantidad,
        opcionFinal
      );

      if (resultado.success) {
        const mensaje = esDisminucion
          ? `Boletos actualizados correctamente. Se disminuyeron ${Math.abs(diferencia)} boleto(s)`
          : `Boletos actualizados correctamente. Se agregaron ${diferencia} boleto(s)`;
        
        showSuccess(mensaje, { duration: 5000 });
        
        // Llamar al callback para actualizar la vista
        if (onBoletosActualizados) {
          onBoletosActualizados();
        }
      } else {
        showError(resultado.error || "Error al actualizar los boletos");
      }
    } catch (error) {
      console.error("Error al actualizar boletos:", error);
      showError("Error al actualizar los boletos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-4">
        <Dialog.Panel className="mx-auto max-w-lg w-full max-h-[90vh] flex flex-col bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl overflow-hidden">
          {/* Header (Fijo) */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-[#1e1e1e]">
            <div className="flex items-center gap-3">
              <div className="bg-casal/10 dark:bg-casal/20 rounded-lg p-2">
                <Ticket className="w-5 h-5 text-casal dark:text-Acapulco" />
              </div>
              <div>
                <Dialog.Title className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                  {esDisminucion ? "Disminuir Boletos" : "Aumentar Boletos"}
                </Dialog.Title>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {deuda.asistente.nombre_completo}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form & Scrollable Body */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 custom-scrollbar">
              {/* Info actual */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60 rounded-xl p-3.5">
                <div className="flex items-start gap-3">
                  <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="text-blue-900 dark:text-blue-100 font-semibold mb-1">
                      Información actual
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-blue-700 dark:text-blue-300">
                      <p>Boletos actuales: <span className="font-semibold">{cantidadActual}</span></p>
                      <p>Precio por boleto: <span className="font-semibold">${precioPorBoleto.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Aviso solo si el usuario intenta ingresar una cantidad menor a la permitida por pagos */}
              {esInvalidaDisminucion && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-0.5">Cancelación de boletos con pagos</p>
                    <p>
                      No es posible disminuir a <strong>{numNuevaCantidad}</strong> boleto(s). Este graduado cuenta con <strong>{boletosPagadosOConAbono}</strong> boleto(s) ya pagados o con abonos. La cantidad mínima permitida es <strong>{minimaCantidadPermitida}</strong> boleto(s).
                    </p>
                    <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                      Para cancelar boletos que ya cuentan con pagos registrados y solicitar un reembolso de dinero, debes realizar el trámite en el módulo de <strong>Devoluciones</strong>.
                    </p>
                  </div>
                </div>
              )}

              {/* Selector de cantidad */}
              <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5 uppercase tracking-wide">
                  Nueva cantidad de boletos
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleDecrement}
                    disabled={numNuevaCantidad <= 1}
                    className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-200/60 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                  </button>
                  
                  <input
                    type="number"
                    value={nuevaCantidad}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    min="1"
                    className={`w-20 text-center text-xl font-bold py-2 px-2 border-2 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 ${
                      esInvalidaDisminucion
                        ? "border-amber-500 focus:ring-amber-500 text-amber-600 dark:text-amber-400"
                        : "border-casal/30 dark:border-casal/50 focus:ring-casal"
                    }`}
                  />
                  
                  <button
                    type="button"
                    onClick={handleIncrement}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-casal hover:bg-casal/90 transition-colors shadow-sm"
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>

                  {diferencia !== 0 && (
                    <div className="ml-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        diferencia > 0 
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" 
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}>
                        {diferencia > 0 ? "+" : ""}{diferencia} boleto{Math.abs(diferencia) !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Opción de prorrateo - Solo para aumento */}
              {!esDisminucion && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                    Gestión de facturas
                  </label>
                  <div className="space-y-2.5">
                    <label className={`flex items-start gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                      opcionProrrateo === "crear_nuevas"
                        ? "border-casal bg-casal/5 dark:bg-casal/10"
                        : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    }`}>
                      <input
                        type="radio"
                        name="prorrateo"
                        value="crear_nuevas"
                        checked={opcionProrrateo === "crear_nuevas"}
                        onChange={(e) => setOpcionProrrateo(e.target.value)}
                        className="mt-0.5 w-4 h-4 text-casal focus:ring-casal"
                      />
                      <div className="flex-1 text-xs">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          Crear nuevas facturas
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                          Se crearán facturas adicionales solo para los boletos nuevos
                        </p>
                      </div>
                    </label>

                    <label className={`flex items-start gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                      opcionProrrateo === "aumentar_existentes"
                        ? "border-casal bg-casal/5 dark:bg-casal/10"
                        : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    }`}>
                      <input
                        type="radio"
                        name="prorrateo"
                        value="aumentar_existentes"
                        checked={opcionProrrateo === "aumentar_existentes"}
                        onChange={(e) => setOpcionProrrateo(e.target.value)}
                        className="mt-0.5 w-4 h-4 text-casal focus:ring-casal"
                      />
                      <div className="flex-1 text-xs">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          Prorratear en facturas existentes
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mt-0.5">
                          El costo adicional se distribuirá en las facturas pendientes
                        </p>
                        {opcionProrrateo === "aumentar_existentes" && (
                          <p className="text-[11px] text-casal dark:text-Acapulco mt-2 font-medium bg-casal/10 dark:bg-casal/20 p-2 rounded-lg border border-casal/20">
                            ℹ️ <strong>Nota:</strong> Solo se puede prorratear en facturas sin abonos ni pagos. Si no existen facturas sin pagos, se crearán facturas nuevas automáticamente.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Resumen del cambio */}
              {diferencia !== 0 && (
                <div className={`border rounded-xl p-3.5 ${
                  esDisminucion
                    ? "bg-red-50/80 dark:bg-red-950/20 border-red-200 dark:border-red-800/50"
                    : "bg-casal/5 dark:bg-casal/10 border-casal/20 dark:border-casal/40"
                }`}>
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      esDisminucion
                        ? "text-red-600 dark:text-red-400"
                        : "text-casal dark:text-Acapulco"
                    }`} />
                    <div className="flex-1 text-xs">
                      <p className="font-semibold text-gray-900 dark:text-white mb-1.5">
                        Resumen del cambio
                      </p>
                      <div className="space-y-1 text-gray-700 dark:text-gray-300">
                        {esDisminucion ? (
                          <>
                            <p>• Se eliminarán <span className="font-semibold">{Math.abs(diferencia)}</span> boleto{Math.abs(diferencia) !== 1 ? "s" : ""}</p>
                            <p>• Reducción de monto: <span className="font-semibold">${montoReduccion.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                            <p>• Nuevo total: <span className="font-semibold">${(deuda.financiero.monto_total - montoReduccion).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                            <p className="text-[11px] text-casal dark:text-Acapulco mt-1 font-medium">ℹ️ Se redistribuirá la deuda en las facturas según el esquema del evento</p>
                          </>
                        ) : (
                          <>
                            <p>• Se agregarán <span className="font-semibold">{diferencia}</span> boleto{diferencia !== 1 ? "s" : ""}</p>
                            <p>• Costo adicional: <span className="font-semibold">${costoAdicional.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                            <p>• Nuevo total: <span className="font-semibold">${(deuda.financiero.monto_total + costoAdicional).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer (Fijo en la parte inferior) */}
            <div className="flex items-center justify-end gap-2.5 px-5 py-3.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !cantidadValida || diferencia === 0 || esInvalidaDisminucion}
                className="px-4 py-2 text-xs sm:text-sm bg-casal hover:bg-casal/90 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                    Procesando...
                  </>
                ) : (
                  <>
                    {esDisminucion ? (
                      <>
                        <Minus className="w-3.5 h-3.5" />
                        Disminuir boletos
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5" />
                        Aumentar boletos
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ModalAumentarBoletos;
